import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import AdminLayout from './AdminLayout';
import './AdminPage.css';

const PAGE_SIZE = 20;

const CONTENT_TYPES = {
    comments: {
        label: 'Комментарии',
        singular: 'комментарий',
        load: (page) => ApiService.getRejectedComments(page),
        publish: (id) => ApiService.publishRejectedComment(id),
    },
    reviews: {
        label: 'Отзывы',
        singular: 'отзыв',
        load: (page) => ApiService.getRejectedReviews(page),
        publish: (id) => ApiService.publishRejectedReview(id),
    },
};

const isAccessDenied = (value) => (
    Number(value?.data?.code ?? value?.response?.data?.data?.code) === 6
);

const formatDate = (value) => {
    const date = new Date(Number(value));
    return Number.isNaN(date.getTime())
        ? 'Дата неизвестна'
        : date.toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' });
};

const formatDateTimeAttribute = (value) => {
    const date = new Date(Number(value));
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const ModerationPage = () => {
    const [contentType, setContentType] = useState('comments');
    const [page, setPage] = useState(0);
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [accessDenied, setAccessDenied] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        document.title = 'Модерация публикаций — animix';
    }, []);

    useEffect(() => {
        let active = true;

        const fetchRecords = async () => {
            setLoading(true);
            setError('');
            setAccessDenied(false);

            try {
                const response = await CONTENT_TYPES[contentType].load(page);
                if (!active) return;

                if (isAccessDenied(response)) {
                    setRecords([]);
                    setAccessDenied(true);
                    return;
                }

                if (response?.type !== 'success') {
                    throw new Error('Unexpected moderation response');
                }

                setRecords(Array.isArray(response.data) ? response.data.slice(0, PAGE_SIZE) : []);
            } catch (requestError) {
                if (!active) return;

                console.error('Error fetching rejected publications:', requestError);
                setRecords([]);
                if (isAccessDenied(requestError)) {
                    setAccessDenied(true);
                } else if (!localStorage.getItem('sessionKey')) {
                    setError('Для просмотра очереди необходимо войти в аккаунт модератора.');
                } else {
                    setError('Не удалось загрузить очередь модерации. Попробуйте ещё раз.');
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchRecords();
        return () => { active = false; };
    }, [contentType, page, refreshKey]);

    const refresh = useCallback(() => {
        setSuccess('');
        setRefreshKey((current) => current + 1);
    }, []);

    const selectContentType = (nextType) => {
        if (nextType === contentType) return;
        setContentType(nextType);
        setPage(0);
        setSuccess('');
    };

    const publishRecord = async (record) => {
        const type = CONTENT_TYPES[contentType];
        if (!window.confirm(`Опубликовать ${type.singular} пользователя ${record.userNickname || `ID ${record.userId}`}?`)) {
            return;
        }

        setBusyId(record.id);
        setError('');
        setSuccess('');

        try {
            const response = await type.publish(record.id);
            if (isAccessDenied(response)) {
                setAccessDenied(true);
                return;
            }
            if (response && response.type !== 'success') {
                throw new Error('Unexpected moderation response');
            }

            setRecords((current) => current.filter((item) => item.id !== record.id));
            setSuccess(`${contentType === 'comments' ? 'Комментарий опубликован' : 'Отзыв опубликован'} вручную.`);
            if (records.length === 1 && page > 0) {
                setPage((current) => current - 1);
            } else {
                setRefreshKey((current) => current + 1);
            }
        } catch (requestError) {
            console.error('Error publishing rejected publication:', requestError);
            if (isAccessDenied(requestError)) {
                setAccessDenied(true);
            } else {
                setError('Не удалось опубликовать запись. Попробуйте ещё раз.');
            }
        } finally {
            setBusyId(null);
        }
    };

    const changePage = (nextPage) => {
        setPage(Math.max(0, Number(nextPage) || 0));
        setSuccess('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <AdminLayout
            mainClassName="admin-list-shell moderation-shell"
            eyebrow="Очередь модератора"
            title="Забракованные публикации"
            lead="Проверьте причину отклонения и при необходимости верните запись в публичную выдачу."
            actions={(
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={refresh} disabled={loading}>
                    {loading ? 'Обновление…' : 'Обновить'}
                </button>
            )}
        >
            <div className="moderation-toolbar">
                <div className="ui-chips moderation-tabs" role="tablist" aria-label="Тип публикации">
                    {Object.entries(CONTENT_TYPES).map(([key, type]) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={contentType === key}
                            className="ui-chip"
                            onClick={() => selectContentType(key)}
                        >
                            {type.label}
                        </button>
                    ))}
                </div>
                <span className="moderation-page-count">До {PAGE_SIZE} записей на странице</span>
            </div>

            {accessDenied ? (
                <section className="moderation-access-state" role="alert">
                    <span className="moderation-access-code">Ошибка 6</span>
                    <h2>Нет доступа к модерации</h2>
                    <p>Эта страница доступна только авторизованным пользователям с правами модератора.</p>
                    {!localStorage.getItem('sessionKey') ? <Link className="ui-btn ui-btn--sm admin-button" to="/login">Войти</Link> : null}
                </section>
            ) : (
                <>
                    {success ? <div className="admin-notice success" role="status">{success}</div> : null}
                    {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
                    {loading ? <div className="admin-state">Загрузка очереди…</div> : null}
                    {!loading && !error && records.length === 0 ? (
                        <div className="admin-state moderation-empty">
                            <strong>Здесь пока пусто</strong>
                            <p>На этой странице нет забракованных {contentType === 'comments' ? 'комментариев' : 'отзывов'}.</p>
                        </div>
                    ) : null}

                    <div className="moderation-record-list">
                        {records.map((record) => (
                            <article key={`${contentType}-${record.id}`} className="moderation-record">
                                <div className="moderation-record-heading">
                                    <div>
                                        <span className="moderation-type-badge">
                                            {contentType === 'comments' ? 'Комментарий' : 'Отзыв'} #{record.id}
                                        </span>
                                        <span className="moderation-status-badge">Отклонено</span>
                                    </div>
                                    <time dateTime={formatDateTimeAttribute(record.date)}>{formatDate(record.date)}</time>
                                </div>

                                <p className="moderation-message">{record.message || 'Пустое сообщение'}</p>

                                <div className="moderation-reason">
                                    <span>Причина отклонения</span>
                                    <strong>{record.moderationReason || 'Причина не указана'}</strong>
                                    <small>Решение принято {formatDate(record.moderatedAt)}</small>
                                </div>

                                <dl className="moderation-meta">
                                    <div>
                                        <dt>Автор</dt>
                                        <dd><Link to={`/profile/${encodeURIComponent(record.userNickname || '')}`}>{record.userNickname || `ID ${record.userId}`}</Link></dd>
                                    </div>
                                    {contentType === 'reviews' ? (
                                        <>
                                            <div>
                                                <dt>Оценка</dt>
                                                <dd><span className="moderation-rating">★ {record.interestRate ?? 0}</span></dd>
                                            </div>
                                            <div>
                                                <dt>Аниме</dt>
                                                <dd>{record.animeId ? <Link to={`/anime/${record.animeId}`}>Тайтл #{record.animeId}</Link> : 'Не указано'}</dd>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="moderation-topic">
                                            <dt>Тема</dt>
                                            <dd>{record.topic || 'Без темы'}</dd>
                                        </div>
                                    )}
                                </dl>

                                <div className="moderation-record-footer">
                                    <span>После публикации запись снова станет видна пользователям.</span>
                                    <button
                                        type="button"
                                        className="ui-btn ui-btn--sm admin-button"
                                        onClick={() => publishRecord(record)}
                                        disabled={busyId !== null}
                                    >
                                        {busyId === record.id ? 'Публикация…' : 'Опубликовать'}
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>

                    <div className="admin-pagination moderation-pagination">
                        <span>Страница {page + 1}</span>
                        <div className="admin-pagination-controls">
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => changePage(page - 1)} disabled={loading || page === 0}>Назад</button>
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => changePage(page + 1)} disabled={loading || records.length < PAGE_SIZE}>Дальше</button>
                            <input
                                type="number"
                                min="1"
                                aria-label="Номер страницы"
                                value={page + 1}
                                onChange={(event) => changePage(Number(event.target.value || 1) - 1)}
                            />
                        </div>
                    </div>
                </>
            )}
        </AdminLayout>
    );
};

export default ModerationPage;
